<template>
  <div class="container">
    <!-- Action Buttons Row -->
    <div class="action-buttons">
      <button @click="managePromotions()" class="btn button-secondary">Manage Promotions</button>
      <button @click="createPromotion()" class="btn button-default">Create Promotion</button>
    </div>

    <!-- Filters -->
    <div class="filter-container">
      <!--Pick from date-->
      <div class="filter-item">
        <label for="startDatePicker">Start Date</label>
        <input type="date" v-model="startDate" class="search-input" />
      </div>

      <!--Pick to date-->
      <div class="filter-item">
        <label for="endDatePicker">End Date</label>
        <input type="date" v-model="endDate" class="search-input" />
      </div>

      <!-- Promotion Name -->
      <div class="filter-item promotion-name-item">
        <label for="status">Promotion Name/Code</label>
        <input
            type="text"
            v-model="promotionName"
            class="search-input promotion-name-input"
            placeholder="Search by promotion name or code..."
        />
      </div>

      <!-- Promotion Type Filter -->
      <div class="filter-item promotion-type">
        <label for="promotionType">Promotion Type</label>
        <select v-model="selectedPromotionType">
          <option value="">All</option>
          <option value="Free Issue">Free Issue</option>
          <option value="Discount">Discount</option>
          <option value="Competitor Price Discount">Competitor Price Discount</option>
          <option value="Trust">Trust</option>
        </select>
      </div>
    </div>

    <!-- Show Loading Spinner -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Show Data Table -->
    <div v-if="!loading && promotions.length > 0" class="table-container">
      <table>
        <thead>
          <tr>
            <th>Promo Code</th>
            <th>Promotion Name</th>
            <th>Promotion Type</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="promo in filteredPromotions" :key="promo.id">
            <td>{{ promo.code }}</td>
            <td>{{ promo.name }}</td>
            <td>{{ promo.type }}</td>
            <td>{{ promo.startDate }}</td>
            <td>{{ promo.endDate }}</td>
            <td>
              <span
                  class="status-badge"
                  :class="{
                  pending: promo.status === '0',
                  active: promo.status === '1',
                  rejected: promo.status === '2',
                  deactivated: promo.status === '3'
                }"
              >
                {{
                  promo.status === '0'
                    ? 'Pending'
                    : promo.status === '1'
                      ? 'Active'
                      : promo.status === '2'
                        ? 'Rejected'
                        : 'Deactivated'
                }}
              </span>
            </td>
            <td class="promo-actions">
              <button v-if="!promo.isApplied && promo.status !== '2'" class="icon-button" @click="editPromotion(promo.id)">
                <i class="fa fa-edit"></i>
              </button>
              <button class="icon-button" @click="viewPromotion(promo.id)">
                <i class="fa fa-eye"></i>
              </button>
            </td>
          </tr>
          <tr v-if="filteredPromotions.length === 0">
            <td colspan="7" style="text-align:center; color: gray;">No promotions found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Show No Data Found Component  -->
    <div v-if="!loading && promotions.length === 0" class="not-found-widget">
      <img src="../../../../assets/vectors/data_not_found.png" alt="No Promotions" class="error-image" />
      <p class="text-center error-message">Promotions Not Found!</p>
    </div>
  </div>
</template>

<script>
import { fetchAllPromotions } from "@/services/dataService";

export default {
  data() {
    return {
      token: localStorage.getItem('token_set'), //Get the API access token from the local storage
      profileId: localStorage.getItem('profile_id'), //Get the profileId from localStorage
      reportingProfileId: localStorage.getItem("reportingL1ProfileId"),
      userCategory: localStorage.getItem("user_label"),
      startDate: "",
      endDate: "",
      promotionName: "",
      selectedPromotionType: "",
      promotions: [],
      loading: false,
      selectedPromotion: null,
    };
  },

  computed: {
    filteredPromotions() {
      return this.promotions
        .filter((promo) => {
          const promoStart = new Date(promo.startDate);
          const promoEnd = new Date(promo.endDate);

          if (this.startDate && new Date(this.startDate) > promoEnd) return false;
          if (this.endDate && new Date(this.endDate) < promoStart) return false;

          if (
              this.promotionName &&
              !(
                  promo.name.toLowerCase().includes(this.promotionName.toLowerCase()) ||
                  promo.code.toLowerCase().includes(this.promotionName.toLowerCase())
              )
          ) {
            return false;
          }

          if (this.selectedPromotionType && promo.type !== this.selectedPromotionType) {
            return false;
          }

          return true;
        })
        // sort promotions by priority (lowest number first)
        .sort((a, b) => a.priority - b.priority);
    },
  },

  mounted() {
    //Call fetch promotions list API
    this.fetchPromotionsList();
  },

  methods: {
    //Call API to get the all promotions list
    async fetchPromotionsList() {
      try {
        //Set Loading Status
        this.loading = true;

        //Reset Array
        this.promotions = [];

        let idToUse = this.profileId;
        if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
          idToUse = this.reportingProfileId;
        }

        const response = await fetchAllPromotions(this.token, idToUse);
        if (response.data.status === "success") {
          this.promotions = response.data.data.map(promotion => ({
            id: promotion.id,
            code: promotion.promotion_code,
            name: promotion.promotion_name,
            type: promotion.promotion_type,
            startDate: promotion.start_date,
            endDate: promotion.end_date,
            status: promotion.status,
            priority: promotion.priority,
            isApplied: promotion.promotion_applied,
          }));
        } else {
          console.log(response.data.message);
        }
      } catch (e) {
        console.error("Failed to fetch promotions list.");
      } finally {
        this.loading = false;
      }
    },

    //Navigate to Add Promotion Screen
    createPromotion() {
      this.$router.push('/create-promotion');
    },

    //Navigate to Edit Promotion Screen
    editPromotion(id) {
      this.$router.push({
        path: '/edit-promotion',
        query: { promoId: id }   // pass as query param
      });
    },

    //Navigate to View Promotion Screen
    viewPromotion(id) {
      const promo = this.promotions.find(p => p.id === id);

      this.$router.push({
        path: '/view-promotion',
        query: {
          promoId: id,
          isApplied: promo.isApplied   // ✅ add this
        }
      });
    },

    //Navigate to Manage Promotion Screen
    managePromotions() {
      this.$router.push('/manage-promotions');
    },

    //Refresh Page (Re-Call API) to load the promotion list
    //When new promotion created, existing promotion updated, manage the existing promotion priority order
    reloadPage() {
      // refresh the list after modal closes
      this.fetchPromotionsList();
    },
  },
};
</script>

<style scoped>
/* Ensure the body and html take the full height of the page */
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0; /* Background color for the page */
}

/* Container for the entire section */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Center items vertically */
  height: 100%; /* Take full height of the page */
  width: 100%; /* Take full width of the page */
  box-sizing: border-box; /* Ensure padding doesn't affect width */
}

/* Action buttons row */
.action-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  width: 100%;
  justify-content: flex-end; /* Align left */
}

/* Base button style */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s ease-in-out;
  color: #fff;
}

/* Container for filter */
.filter-container {
  display: grid;
  grid-template-columns: auto auto auto auto; /* All columns fit naturally */
  gap: 10px; /* Spacing between items */
  width: 100%; /* Ensure container takes full width */
  justify-content: start; /* Align items to start */
  align-items: end; /* Align items to bottom for consistent baseline */
  margin-bottom: 10px;
}

/* Each filter item with label */
.filter-item {
  display: flex;
  flex-direction: column;
  min-width: fit-content; /* Adjust to content size */
}

/* Special styling for promotion name item with fixed width */
.promotion-name-item {
  width: 340px; /* Fixed width for consistent layout */
}

/* Special styling for promotion type item with fixed width */
.promotion-type {
  width: 180px; /* Fixed width for consistent layout */
}

/* Styling for labels */
label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px; /* Adjust font size for better readability */
}

/* Styling for date-picker */
input[type="date"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
}

/* Styling for text box */
input[type="text"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
}

/* Styling for promotion name input */
.promotion-name-input {
  width: 100%; /* Full width inside fixed container */
}

/* Styling for dropdown */
select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  height: 40px;
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff; /* Preserved color for spinner */
}

/* Scrollable Table Container */
.table-container {
  width: 100%;
  overflow-x: auto;
}

/* Table */
table {
  width: 100%;
  min-width: 800px; /* ensure scroll if too many cols */
  border-collapse: collapse;
  margin: 10px 0;
  border: none; /* remove table border */
}

/* Table Header */
th {
  text-align: left;
  border-bottom: 2px solid #ddd; /* subtle bottom border for header */
  background-color: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
}

/* Table Data */
td {
  text-align: left;
  border: none; /* remove individual cell borders */
  padding: 10px;
  white-space: nowrap;
  border-bottom: 1px solid #eee; /* row separator */
}

/* Optional: remove last row separator for cleaner look */
tbody tr:last-child td {
  border-bottom: none;
}

/* Styling for icon button */
.icon-button {
  margin: 0;
  font-size: 15px;
  background-color: transparent;
  border: none;
}
.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}
.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-eye:active {
  transform: scale(1.1);
}
.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}
.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-edit:active {
  transform: scale(1.1);
}

/* Status badge (chip) */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 13px;
  border-radius: 5px;
  text-align: center;
  width: 100px;       /* fixed width for uniform size */
  max-width: 100px;   /* prevent larger width */
  min-width: 100px;   /* prevent shrinking */
  color: #fff;
  box-sizing: border-box; /* include padding inside width */
}

/* Pending = Amber */
.status-badge.pending {
  background-color: #ffb300; /* Amber */
}

/* Active = Green */
.status-badge.active {
  background-color: #2e7d32;
}

/* Rejected = Red */
.status-badge.rejected {
  background-color: #c62828;
}

/* Deactivated = Black */
.status-badge.deactivated {
  background-color: #212121;
}

/* Promotion Not Found Widget */
.not-found-widget {
  max-width: 500px;
}

/* Promotion Not Found Widget - Image */
.not-found-widget img {
  width: 250px;
}

/* Promotion Not Found Widget - Message */
.not-found-widget p {
  color: grey;
  font-size: 18px;
  font-weight: bold;
  margin-top: -25px;
}

/* Modal Overlay Style */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
</style>